/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { CompanyDTO, CompanyFilterDTO, OpeningDTO, OpeningFilterDTO, UserDTO, UserFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';

interface IOpeningMaintenanceProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const OpeningMaintenance: React.FC<IOpeningMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {

    const [salesPerscpectiveList, setSalesPerspectiveList] = useState<ValueListDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [openingDTO, setOpeningDTO] = useState<OpeningDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setOpeningDTO({
            ...openingDTO,
            id: '',
            code: '',
            laboratory: '',
            salePerspectiveId: '',
            description: '',
            information: '',
            developmentDeadline: '',
            companyId: ''
        } as OpeningDTO);

        if(isOpenMaintenance){
            handleListGetAll(ListId.corpsphere.opening.salesPerspective, setSalesPerspectiveList);
            handleCompanyGetAll();
            handleUserGetAllAsync(setUserList);
            handleGetOpeningByIdAsync();
        }
    }, [data, isOpenMaintenance]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleUserGetAllAsync = useCallback(async (setUserList: any) => {
        const userFilterDTO: UserFilterDTO = {
            companyId: '',
            peopleId: '',
            userName: '',
            isServiceAccount: false
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleGetOpeningByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('corpsphere.opening', data.id, setLoading, setOpeningDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = openingDTO as OpeningDTO;

        if (openingDTO?.id === '' || openingDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('corpsphere.opening', dto, setOpeningDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('corpsphere.opening', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [openingDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='SDT - OPENING DETAILS'
                icon='grid_view_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={2}>
                            <TextFieldInput
                                id='code'
                                label='SDT Code'
                                value={openingDTO?.code}
                                setValue={setOpeningDTO}
                                obj={openingDTO}
                                required={true}
                                maxLength={20}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldInput
                                id='laboratory'
                                label='Laboratory'
                                value={openingDTO?.laboratory}
                                setValue={setOpeningDTO}
                                obj={openingDTO}
                                required={true}
                                maxLength={20}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='salePerspectiveId'
                                label='Sales Perspective'
                                placeholder='Sales Perspective'
                                value={openingDTO?.salePerspectiveId}
                                setValue={setOpeningDTO}
                                obj={openingDTO}
                                valueList={salesPerscpectiveList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldInput
                                id='developmentDeadline'
                                label='Development Deadline (DU)'
                                value={openingDTO?.developmentDeadline}
                                setValue={setOpeningDTO}
                                obj={openingDTO}
                                maxLength={20}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='companyId'
                                label='Customer'
                                placeholder='Customer'
                                value={openingDTO?.companyId}
                                setValue={setOpeningDTO}
                                obj={openingDTO}
                                valueList={companyList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label='Reason/Project'
                                value={openingDTO?.description}
                                setValue={setOpeningDTO}
                                obj={openingDTO}
                                maxLength={4000}
                                multiline={true}
                                rows={12}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='information'
                                label='Additional information'
                                value={openingDTO?.information}
                                setValue={setOpeningDTO}
                                obj={openingDTO}
                                maxLength={4000}
                                multiline={true}
                                rows={6}
                            />
                        </Grid>
                        {openingDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={openingDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === openingDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(openingDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === openingDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(openingDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};